import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useParams, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  useTheme,
  useMediaQuery,
  Container,
  Stack,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Divider,
  Menu,
  MenuItem,
  Collapse,
  ListItemButton,
  Chip,
  Badge
} from '@mui/material';

import {
  Menu as MenuIcon,
  PersonAdd as PersonAddIcon,
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  ExitToApp as ExitToAppIcon,
  AssignmentInd as AssignmentIndIcon,
  Person as PersonIcon,
  ExpandLess,
  ExpandMore,
  AddCircle as AddCircleIcon,
  Group as GroupIcon,
  Home as HomeIcon,
  Login as LoginIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Message as MessageIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { buildOrgUrl } from '../utils/organizationContext';
import useOrganizationName from '../hooks/useOrganizationName';

const Navigation = ({ isAuthenticated, onLogout, user }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const params = useParams();
  
  // Extract organization slug from URL pathname or params
  // Path format: /toronto/dashboard, /kitchener/login, etc.
  const getOrganizationSlug = () => {
    // First try useParams (works if Navigation is inside Routes)
    if (params.organizationSlug) {
      return params.organizationSlug;
    }
    
    // Otherwise, parse from pathname
    const pathParts = location.pathname.split('/').filter(Boolean);
    // Check if first part is a valid organization slug (not a known route)
    const knownRoutes = ['admin', 'master-dashboard', 'communication-analytics', 'organization-management', 'user-management'];
    if (pathParts.length > 0 && !knownRoutes.includes(pathParts[0])) {
      return pathParts[0];
    }

    if (user?.organization_slug) {
      return user.organization_slug;
    }
    
    return null;
  };
  
  const organizationSlug = getOrganizationSlug();
  const { organizationName } = useOrganizationName();
  
  // Helper function to build organization-prefixed URLs
  const buildPath = (path) => {
    const isSuperAdmin = user?.role === 'system_admin' && !user?.organization_id;
    const superAdminRoutes = ['/master-dashboard', '/communication-analytics', '/organization-management', '/user-management', '/admin/login'];
    const effectiveOrgSlug = organizationSlug || user?.organization_slug;
    
    // Don't prefix super-admin routes
    if (isSuperAdmin && superAdminRoutes.includes(path)) {
      return path;
    }
    
    // Don't prefix if no organization slug (e.g., on welcome page)
    if (!effectiveOrgSlug) {
      return path;
    }
    
    return buildOrgUrl(path, effectiveOrgSlug);
  };
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [menuAnchors, setMenuAnchors] = useState({});
  const [openMobileMenus, setOpenMobileMenus] = useState({});
  const [taskCounts, setTaskCounts] = useState({ myTasks: 0, notifications: 0 });
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);
  const [notificationMenuAnchor, setNotificationMenuAnchor] = useState(null);
  
  // Branch display name in header (full org name when available)
  const orgDisplayName = organizationSlug
    ? (organizationName?.replace(/^Winners Chapel International\s?/i, '') || organizationSlug)
    : 'Canada';

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleAccountMenuClick = (event) => {
    setAccountMenuAnchor(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null);
  };

  const handleLogoutClick = () => {
    onLogout();
    handleAccountMenuClose();
  };

  const handleNotificationMenuClick = (event) => {
    setNotificationMenuAnchor(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationMenuAnchor(null);
  };

  const handleNavMenuOpen = (event, menuId) => {
    setMenuAnchors(prev => ({
      ...prev,
      [menuId]: event.currentTarget
    }));
  };

  const handleNavMenuClose = (menuId) => {
    setMenuAnchors(prev => ({
      ...prev,
      [menuId]: null
    }));
  };

  const handleMobileMenuToggle = (menuId) => {
    setOpenMobileMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  // Fetch task counts for notifications and badges
  const fetchTaskCounts = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    
    // Skip fetching tasks for super-admins without organization context
    // Super-admins don't have organization-specific tasks
    if (user.role === 'system_admin' && !user.organization_id) {
      setTaskCounts({ myTasks: 0, notifications: 0 });
      return;
    }
    
    setIsLoadingCounts(true);
    
    try {
      const token = localStorage.getItem('token');
      console.log('Token exists:', !!token);
      if (!token) return;

      // Fetch my tasks count from assignments endpoint
      console.log('Fetching tasks for user:', user?.id);
      console.log('REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
      console.log('API URL:', `${process.env.REACT_APP_API_URL}/api/assignments/my-tasks`);
      
      const myTasksResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/assignments/my-tasks`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Response status:', myTasksResponse.status);
      
      if (myTasksResponse.ok) {
        const myTasksData = await myTasksResponse.json();
        console.log('My tasks response:', myTasksData);
        const myTasksCount = myTasksData.total || 0;
        console.log('My tasks count:', myTasksCount);
        
        // For notifications, we'll use the same count since these are tasks assigned to the user
        setTaskCounts({
          myTasks: myTasksCount,
          notifications: myTasksCount
        });
        console.log('Set task counts to:', { myTasks: myTasksCount, notifications: myTasksCount });
      } else {
        const errorText = await myTasksResponse.text();
        console.error('Failed to fetch my tasks:', myTasksResponse.status, errorText);
        setTaskCounts({ myTasks: 0, notifications: 0 });
      }
    } catch (error) {
      console.error('Error fetching task counts:', error);
      setTaskCounts({ myTasks: 0, notifications: 0 });
    } finally {
      setIsLoadingCounts(false);
    }
  }, [isAuthenticated, user]);

  // Fetch task counts when component mounts or user changes
  useEffect(() => {
    console.log('Navigation useEffect triggered - fetching task counts');
    console.log('User state:', { isAuthenticated, user: user?.id });
    fetchTaskCounts();
    
    // Refresh counts every 30 seconds
    const interval = setInterval(fetchTaskCounts, 30000);
    
    return () => clearInterval(interval);
  }, [fetchTaskCounts, isAuthenticated, user]);

  const getMenuItems = () => {
    const menuStructure = [];
    const isSuperAdmin = user?.role === 'system_admin' && !user?.organization_id;

    if (isAuthenticated) {
      // Platform super-admin only: national overview and all-branch management
      if (isSuperAdmin) {
        menuStructure.push({
          text: 'National Overview',
          icon: <DashboardIcon />,
          path: '/master-dashboard'
        });

        menuStructure.push({
          text: 'Organizations',
          icon: <BusinessIcon />,
          path: '/organization-management'
        });

        menuStructure.push({
          text: 'Manage Administration',
          icon: <PeopleIcon />,
          path: '/user-management'
        });

        return menuStructure;
      }

      // Regular menu for organization-specific users (including org admins)
      // Personal section - available to all authenticated users
      menuStructure.push({
        id: 'personal',
        label: 'Personal',
        icon: <PersonIcon />,
        items: [
          { text: 'My Tasks', icon: <AssignmentIcon />, path: buildPath('/my-tasks'), badge: taskCounts.myTasks.toString() },
          { text: 'My New Converts', icon: <AssignmentIcon />, path: buildPath('/my-new-converts') },
          { text: 'My Profile', icon: <PersonIcon />, path: buildPath('/profile') }
        ]
      });

      // First Timer Management section
      menuStructure.push({
        id: 'first-timers',
        label: 'First Timer Management',
        icon: <GroupIcon />,
        items: [
          { text: 'Add First Timer', icon: <AddCircleIcon />, path: buildPath('/add-first-timer') },
          ...(user?.role === 'unit_manager' || user?.role === 'system_admin' ? [
            { text: 'Follow-Up Dashboard', icon: <DashboardIcon />, path: buildPath('/dashboard') },
            { text: 'Assign First Timers', icon: <AssignmentIndIcon />, path: buildPath('/assignments') }
          ] : [])
        ]
      });

      // New Convert Management section - dashboard restricted to managers and admins
      menuStructure.push({
        id: 'new-converts',
        label: 'New Convert Management',
        icon: <PersonAddIcon />,
        items: [
          { text: 'New Convert Form', icon: <PersonAddIcon />, path: buildPath('/new-convert') },
          ...(user?.role === 'unit_manager' || user?.role === 'system_admin' ? [
            { text: 'New Convert Dashboard', icon: <DashboardIcon />, path: buildPath('/new-converts') },
            { text: 'Assign New Converts', icon: <AssignmentIndIcon />, path: buildPath('/assign-new-converts') }
          ] : [])
        ]
      });

      // Foundation School Management section
      menuStructure.push({
        id: 'foundation-school',
        label: 'Foundation School Management',
        icon: <SchoolIcon />,
        items: [
          { text: 'Foundation School Form', icon: <AddCircleIcon />, path: buildPath('/foundation-school') },
          ...(user?.role === 'unit_manager' || user?.role === 'system_admin' ? [
            { text: 'Foundation School Dashboard', icon: <DashboardIcon />, path: buildPath('/foundation-school-dashboard') },
            { text: 'Assign Class', icon: <AssignmentIndIcon />, path: buildPath('/assign-foundation-school') }
          ] : [])
        ]
      });

      // Admin section - available to managers and admins
      if (user?.role === 'unit_manager' || user?.role === 'system_admin') {
        menuStructure.push({
          id: 'admin',
          label: 'Administration',
          icon: <PeopleIcon />,
          items: [
            // SMS Center - available to managers and admins
            { text: 'SMS Center', icon: <MessageIcon />, path: buildPath('/sms') },
            // Email Campaigns - available to managers and admins
            { text: 'Email Campaigns', icon: <EmailIcon />, path: buildPath('/email-campaigns') },
            // Harvest Outreach System - available to managers and admins
            { text: 'Harvest Outreach', icon: <GroupIcon />, path: buildPath('/harvest-system') },
            // Admin Management - restricted to system admin only
            ...(user?.role === 'system_admin' ? [
              { text: 'Manage Users', icon: <PeopleIcon />, path: buildPath('/user-management') },
              // Organization admins (with organization_id) can only configure their own
              ...(user?.organization_id ? [
                { text: 'Organization Settings', icon: <SettingsIcon />, path: buildPath('/organization-settings') }
              ] : [])
            ] : [])
          ]
        });
      }
    }

    return menuStructure;
  };

  // Direct menu items for non-authenticated users
  const getPublicMenuItems = () => [
    { text: 'New Convert', icon: <PersonAddIcon />, path: organizationSlug ? buildPath('/new-convert') : '/new-convert' },
    { text: 'First Timer', icon: <GroupIcon />, path: organizationSlug ? buildPath('/add-first-timer') : '/' },
    { text: 'Foundation School', icon: <SchoolIcon />, path: organizationSlug ? buildPath('/foundation-school') : '/foundation-school' },
    { text: 'Harvest Outreach', icon: <GroupIcon />, path: organizationSlug ? buildPath('/harvest') : '/harvest' },
    { text: 'Sign in', icon: <LoginIcon />, path: organizationSlug ? buildPath('/login') : '/admin/login' },
    { text: 'Sign up', icon: <PersonIcon />, path: organizationSlug ? buildPath('/signup') : '/signup' }
  ];

  const renderMenuItem = (item) => {
    if (!item.items) {
      return (
        <ListItem
          button
          key={item.text}
          component={RouterLink}
          to={item.path}
          onClick={handleDrawerToggle}
          sx={{ 
            pl: 4,
            borderRadius: 1,
            mx: 1,
            mb: 0.5,
            '&:hover': {
              backgroundColor: theme.palette.primary.light + '10',
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
          <ListItemText 
            primary={item.text} 
            primaryTypographyProps={{
              sx: { fontWeight: 500 }
            }}
          />
          {item.badge && item.badge !== '0' && (
            <Chip 
              label={item.badge} 
              size="small" 
              color="primary" 
              sx={{ 
                height: 20, 
                fontSize: '0.75rem',
                '& .MuiChip-label': { px: 1 }
              }} 
            />
          )}
        </ListItem>
      );
    }

    return (
      <React.Fragment key={item.id}>
        <ListItemButton 
          onClick={() => handleMobileMenuToggle(item.id)}
          sx={{ 
            borderRadius: 1,
            mx: 1,
            mb: 0.5,
            '&:hover': {
              backgroundColor: theme.palette.primary.light + '10',
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
          <ListItemText 
            primary={item.label} 
            primaryTypographyProps={{
              sx: { fontWeight: 600 }
            }}
          />
          {openMobileMenus[item.id] ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={openMobileMenus[item.id]} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.items.map((subItem) => (
              <ListItem
                button
                key={subItem.text}
                component={RouterLink}
                to={subItem.path}
                onClick={handleDrawerToggle}
                sx={{ 
                  pl: 6,
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light + '10',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{subItem.icon}</ListItemIcon>
                <ListItemText 
                  primary={subItem.text} 
                  primaryTypographyProps={{
                    sx: { fontWeight: 500 }
                  }}
                />
                {subItem.badge && subItem.badge !== '0' && (
                  <Chip 
                    label={subItem.badge} 
                    size="small" 
                    color="primary" 
                    sx={{ 
                      height: 20, 
                      fontSize: '0.75rem',
                      '& .MuiChip-label': { px: 1 }
                    }} 
                  />
                )}
              </ListItem>
            ))}
          </List>
        </Collapse>
      </React.Fragment>
    );
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo header - always visible */}
      <Box sx={{ 
        p: 2, 
        background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
        color: 'white',
        borderRadius: '0 0 16px 16px',
        textAlign: 'center'
      }}>
        <img
          src="/assets/church-logo.png"
          alt="Winners Chapel Logo"
          style={{
            height: '32px',
            width: '32px',
            marginBottom: '8px',
            filter: 'brightness(0) invert(1)',
            borderRadius: '50%',
            objectFit: 'cover'
          }}
        />
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            fontSize: '1.1rem',
            wordWrap: 'break-word',
            textAlign: 'center',
            px: 1
          }}
        >
          Winners Chapel International {orgDisplayName} Follow-Up
        </Typography>
      </Box>
      
      {/* User info - only for authenticated users */}
      {isAuthenticated && (
        <Box sx={{ 
          p: 3, 
          background: 'rgba(255,255,255,0.1)',
          color: 'white',
          borderRadius: '16px',
          mx: 2,
          mb: 2
        }}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                width: 48, 
                height: 48,
                border: '2px solid rgba(255,255,255,0.3)'
              }}
            >
              {user?.first_name?.[0] || 'U'}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {user?.first_name} {user?.last_name}
              </Typography>
              <Chip 
                label={
                  user?.role === 'system_admin' && !user?.organization_id ? 'Super Admin' :
                  user?.role === 'system_admin' && user?.organization_id ? 'Organization Admin' :
                  user?.role === 'unit_manager' ? 'Unit Manager' : 
                  'Unit Member'
                }
                size="small"
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontSize: '0.75rem',
                  height: 20
                }}
              />
              {user?.organization_slug && (
                <Chip 
                  label={organizationName || user.organization_slug}
                  size="small"
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    fontSize: '0.7rem',
                    height: 18,
                    ml: 0.5
                  }}
                />
              )}
            </Box>
          </Box>
          <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
            {user?.email}
          </Typography>
        </Box>
      )}
      
      <Box sx={{ flex: 1, overflow: 'auto', py: 2 }}>
        <List>
          {isAuthenticated ? 
            getMenuItems().map(renderMenuItem) : 
            getPublicMenuItems().map((item) => (
              <ListItem
                button
                key={item.text}
                component={RouterLink}
                to={item.path}
                onClick={handleDrawerToggle}
                sx={{ 
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light + '10',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{
                    sx: { fontWeight: 500 }
                  }}
                />
              </ListItem>
            ))
          }
        </List>
      </Box>
      
      {isAuthenticated && (
        <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <ListItem 
            button 
            onClick={() => {
              onLogout();
              handleDrawerToggle();
            }}
            sx={{ 
              borderRadius: 1,
              '&:hover': {
                backgroundColor: theme.palette.error.light + '10',
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <ExitToAppIcon color="error" />
            </ListItemIcon>
            <ListItemText 
              primary="Logout" 
              primaryTypographyProps={{
                sx: { fontWeight: 500, color: theme.palette.error.main }
              }}
            />
          </ListItem>
        </Box>
      )}
    </Box>
  );

  return (
    <AppBar position="sticky" elevation={0}>
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, py: { xs: 0.5, sm: 1 } }}>
        <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <img
                src="/assets/church-logo.png"
                alt="Winners Chapel Logo"
                style={{
                  height: isMobile ? '28px' : '36px',
                  width: isMobile ? '28px' : '36px',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                  marginRight: '8px',
                  borderRadius: '50%',
                  objectFit: 'cover'
                }}
              />
              <Typography 
                variant={isMobile ? "body1" : "h6"}
                component={RouterLink} 
                to={(user?.role === 'system_admin' && !user?.organization_id) ? "/master-dashboard" : (organizationSlug ? buildPath('/add-first-timer') : "/")} 
                sx={{ 
                  textDecoration: 'none', 
                  color: 'inherit',
                  ml: isMobile ? 1 : 1.5,
                  whiteSpace: isMobile ? 'normal' : 'nowrap',
                  fontWeight: 600,
                  fontSize: isMobile ? '0.875rem' : undefined,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: isMobile ? '200px' : 'none',
                  display: 'block'
                }}
              >
                Winners Chapel International {orgDisplayName} Follow-Up
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" sx={{ ml: 'auto', gap: 2 }}>
              {!isAuthenticated ? (
                isMobile ? (
                  <IconButton
                    color="inherit"
                    onClick={handleDrawerToggle}
                    aria-label="open drawer"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.1)',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.2)',
                      }
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
                ) : (
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <Button
                      component={RouterLink}
                      to={organizationSlug ? buildPath('/new-convert') : "/"}
                      color="inherit"
                      size="small"
                      sx={{ 
                        textTransform: 'capitalize',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        px: 2,
                        py: 0.75,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.1)',
                        }
                      }}
                    >
                      New Convert
                    </Button>
                    <Button
                      component={RouterLink}
                      to={organizationSlug ? buildPath('/add-first-timer') : "/"}
                      color="inherit"
                      size="small"
                      sx={{ 
                        textTransform: 'capitalize',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        px: 2,
                        py: 0.75,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.1)',
                        }
                      }}
                    >
                      First Timer
                    </Button>
                    <Button
                      component={RouterLink}
                      to={organizationSlug ? buildPath('/foundation-school') : "/foundation-school"}
                      color="inherit"
                      size="small"
                      sx={{ 
                        textTransform: 'capitalize',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        px: 2,
                        py: 0.75,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.1)',
                        }
                      }}
                    >
                      Foundation School
                    </Button>
                    <Button
                      component={RouterLink}
                      to={organizationSlug ? buildPath('/harvest') : "/"}
                      color="inherit"
                      size="small"
                      sx={{ 
                        textTransform: 'capitalize',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        px: 2,
                        py: 0.75,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.1)',
                        }
                      }}
                    >
                      Harvest Outreach
                    </Button>
                    <Button
                      component={RouterLink}
                      to={organizationSlug ? buildPath('/login') : "/admin/login"}
                      variant="outlined"
                      color="inherit"
                      size="small"
                      sx={{ 
                        textTransform: 'capitalize',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        px: 2,
                        py: 0.75,
                        borderRadius: 2,
                        borderColor: 'rgba(255,255,255,0.3)',
                        '&:hover': {
                          borderColor: 'rgba(255,255,255,0.5)',
                          bgcolor: 'rgba(255,255,255,0.1)',
                        }
                      }}
                    >
                      Sign in
                    </Button>
                    <Button
                      component={RouterLink}
                      to={organizationSlug ? buildPath('/signup') : "/"}
                      variant="contained"
                      size="small"
                      sx={{ 
                        textTransform: 'capitalize',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        px: 2,
                        py: 0.75,
                        borderRadius: 2,
                        background: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        '&:hover': {
                          background: 'rgba(255,255,255,0.3)',
                        }
                      }}
                    >
                      Sign up
                    </Button>
                  </Stack>
                )
              ) : isMobile ? (
                <IconButton
                  color="inherit"
                  onClick={handleDrawerToggle}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.2)',
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              ) : (
                <>
                  <Stack direction="row" spacing={1}>
                    {isAuthenticated && getMenuItems().map((menuGroup) => (
                      <Box key={menuGroup.id || menuGroup.text}>
                        {!menuGroup.items ? (
                          <Button
                            color="inherit"
                            component={RouterLink}
                            to={menuGroup.path}
                            sx={{
                              textTransform: 'none',
                              fontSize: '0.875rem',
                              fontWeight: location.pathname === menuGroup.path ? 700 : 500,
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 0,
                              borderBottom: location.pathname === menuGroup.path
                                ? '2px solid currentColor'
                                : '2px solid transparent',
                              '&:hover': {
                                bgcolor: 'transparent',
                                borderBottomColor: 'currentColor',
                              }
                            }}
                          >
                            {menuGroup.text}
                          </Button>
                        ) : (
                          <>
                        <Button
                          color="inherit"
                          onClick={(event) => handleNavMenuOpen(event, menuGroup.id)}
                          endIcon={Boolean(menuAnchors[menuGroup.id]) ? <ExpandLess /> : <ExpandMore />}
                          sx={{
                            textTransform: 'capitalize',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            px: 2,
                            py: 0.75,
                            borderRadius: 2,
                            '&:hover': {
                              bgcolor: 'rgba(255,255,255,0.1)',
                            }
                          }}
                        >
                          {menuGroup.label}
                        </Button>
                        <Menu
                          anchorEl={menuAnchors[menuGroup.id]}
                          open={Boolean(menuAnchors[menuGroup.id])}
                          onClose={() => handleNavMenuClose(menuGroup.id)}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                          }}
                          sx={{
                            '& .MuiPaper-root': {
                              minWidth: 220,
                              mt: 1,
                              borderRadius: 2,
                              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                            }
                          }}
                        >
                          {menuGroup.items.map((item) => (
                            <MenuItem
                              key={item.text}
                              component={RouterLink}
                              to={item.path}
                              onClick={() => handleNavMenuClose(menuGroup.id)}
                              sx={{
                                gap: 1.5,
                                minHeight: 44,
                                fontSize: '0.875rem',
                                borderRadius: 1,
                                mx: 0.5,
                                my: 0.25,
                                '&:hover': {
                                  bgcolor: theme.palette.primary.light + '10',
                                }
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 36, color: theme.palette.primary.main }}>
                                {item.icon}
                              </ListItemIcon>
                              <ListItemText 
                                primary={item.text} 
                                primaryTypographyProps={{
                                  sx: { 
                                    fontSize: '0.875rem',
                                    fontWeight: 500
                                  }
                                }}
                              />
                              {item.badge && (
                                <Chip 
                                  label={item.badge} 
                                  size="small" 
                                  color="primary" 
                                  sx={{ 
                                    height: 20, 
                                    fontSize: '0.75rem',
                                    '& .MuiChip-label': { px: 1 }
                                  }} 
                                />
                              )}
                            </MenuItem>
                          ))}
                        </Menu>
                          </>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </>
              )}

              {isAuthenticated && (
                <>
                                      <Tooltip title="View notifications">
                      <IconButton
                        color="inherit"
                        onClick={handleNotificationMenuClick}
                        disabled={isLoadingCounts}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.1)',
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.2)',
                          },
                          '&:disabled': {
                            opacity: 0.6
                          }
                        }}
                      >
                        <Badge 
                          badgeContent={isLoadingCounts ? '...' : (taskCounts.notifications > 0 ? taskCounts.notifications : null)} 
                          color="error"
                        >
                          <NotificationsIcon />
                        </Badge>
                      </IconButton>
                                        </Tooltip>
                    
                    <Menu
                      id="notification-menu"
                      anchorEl={notificationMenuAnchor}
                      open={Boolean(notificationMenuAnchor)}
                      onClose={handleNotificationMenuClose}
                      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                      PaperProps={{
                        sx: { 
                          minWidth: '300px',
                          mt: 1,
                          borderRadius: 2,
                          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                        }
                      }}
                    >
                      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Notifications
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={fetchTaskCounts}
                            disabled={isLoadingCounts}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Current counts: My Tasks: {taskCounts.myTasks}, Notifications: {taskCounts.notifications}
                        </Typography>
                        {taskCounts.notifications === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No new notifications
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            You have {taskCounts.notifications} task(s) assigned to you
                          </Typography>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          component={RouterLink}
                          to={buildPath('/my-tasks')}
                          onClick={handleNotificationMenuClose}
                          sx={{ mt: 2, mr: 1 }}
                        >
                          View My Tasks
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            fetchTaskCounts();
                            handleNotificationMenuClose();
                          }}
                          sx={{ mt: 2 }}
                        >
                          Refresh Counts
                        </Button>
                      </Box>
                    </Menu>
                    
                    <IconButton
                    onClick={handleAccountMenuClick}
                    size="small"
                    aria-controls={Boolean(accountMenuAnchor) ? 'account-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={Boolean(accountMenuAnchor) ? 'true' : undefined}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.1)',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.2)',
                      }
                    }}
                  >
                    <Avatar sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      width: 32, 
                      height: 32,
                      border: '2px solid rgba(255,255,255,0.3)'
                    }}>
                      {user?.first_name?.[0]?.toUpperCase() || 'U'}
                    </Avatar>
                  </IconButton>
                  <Menu
                    id="account-menu"
                    anchorEl={accountMenuAnchor}
                    open={Boolean(accountMenuAnchor)}
                    onClose={handleAccountMenuClose}
                    onClick={handleAccountMenuClose}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    PaperProps={{
                      sx: { 
                        minWidth: '240px',
                        mt: 1,
                        borderRadius: 2,
                        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Avatar sx={{ 
                          bgcolor: theme.palette.primary.main, 
                          width: 40, 
                          height: 40 
                        }}>
                          {user?.first_name?.[0]?.toUpperCase() || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                            {user?.first_name} {user?.last_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {user?.email}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip 
                        label={
                          user?.role === 'system_admin' && !user?.organization_id ? 'Super Admin' :
                          user?.role === 'system_admin' && user?.organization_id ? 'Organization Admin' :
                          user?.role === 'unit_manager' ? 'Unit Manager' :
                          'Unit Member'
                        }
                        size="small"
                        color={
                          user?.role === 'system_admin' && !user?.organization_id ? 'error' :
                          user?.role === 'system_admin' && user?.organization_id ? 'primary' :
                          'default'
                        }
                        sx={{ fontSize: '0.75rem', height: 20 }}
                      />
                      {user?.organization_slug && (
                        <Chip 
                          label={user.organization_slug}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 18, mt: 0.5 }}
                        />
                      )}
                    </Box>
                    <MenuItem 
                      component={RouterLink}
                      to={buildPath('/profile')}
                      sx={{ py: 1.5, gap: 1.5 }}
                    >
                      <ListItemIcon>
                        <PersonIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Profile" 
                        primaryTypographyProps={{
                          sx: { fontSize: '0.875rem', fontWeight: 500 }
                        }}
                      />
                    </MenuItem>
                    <Divider />
                    <MenuItem 
                      onClick={handleLogoutClick} 
                      sx={{ 
                        py: 1.5, 
                        gap: 1.5,
                        '&:hover': {
                          bgcolor: theme.palette.error.light + '10',
                        }
                      }}
                    >
                      <ListItemIcon>
                        <ExitToAppIcon fontSize="small" color="error" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Logout" 
                        primaryTypographyProps={{
                          sx: { 
                            fontSize: '0.875rem', 
                            fontWeight: 500,
                            color: theme.palette.error.main
                          }
                        }}
                      />
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          </Box>
        </Container>
      </Toolbar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            width: 320,
            border: 'none',
            boxShadow: '0 0 20px rgba(0,0,0,0.1)',
          },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default Navigation;
